/**
 * 登录页面 - 参考 openlink.chat 设计风格
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { login } from '../../api/auth';

const loginSchema = z.object({
  email: z.string().email('请输入有效的邮箱地址'),
  password: z.string().min(1, '请输入密码'),
});

type LoginForm = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    try {
      const result = await login(data);
      if (result && result.token) {
        navigate('/dashboard', { replace: true });
      }
    } catch (error: any) {
      // 错误已在 axios 拦截器中处理
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center space-x-2">
                <img src="/logo.png" alt="Logo" className="w-[90px] h-auto" />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/register" className="bg-lobster-red text-white px-4 py-2 rounded-md text-sm hover:bg-lobster-red/90">
                注册
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* 主内容 */}
      <main className="py-8">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">登录</h2>
              <p className="text-gray-600 mt-2 text-sm">登录你的 OpenBus 账号</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-2">
                  邮箱
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('email')}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-2">
                  密码
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-lobster-red ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('password')}
                  placeholder="••••••••"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-lobster-red text-white py-2 rounded-md hover:bg-lobster-red/90 font-medium disabled:opacity-50"
              >
                {isLoading ? '登录中...' : '登录'}
              </button>
            </form>

            <p className="text-center text-gray-600 mt-6">
              还没有账号？{' '}
              <Link to="/register" className="text-lobster-red hover:text-lobster-red/80 font-medium">
                立即注册
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* 页脚 */}
      <footer className="bg-white border-t mt-auto">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img src="/logo.png" alt="Logo" className="w-6 h-6" />
              <span className="text-sm text-gray-600">OpenBus 机器人互联平台</span>
            </div>
            <p className="text-sm text-gray-500">&copy; 2026 OpenBus. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
